import dynamic from 'next/dynamic';

import clsx from 'clsx';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Rectangle,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import { useFormatTimeIndicator } from '@/hooks/format/useFormatTimeIndicator';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//hook
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';
import { formatXAxisFilters } from '@/lib/charts/formatXAxis';

import Icon from '@/components/base/Icon';
// Components
import TooltipCustom from './components/TooltipCustom';

import ChartWrapper from './ChartWrapper';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const CustomizedAxisTick = (props) => {
    const { x, y, payload, data, isExport } = props;

    const customTickContent = () => {
        switch (data?.customTick?.type) {
            case 'reaction':
                return (
                    <Icon
                        className={clsx('text-base text-icoGray-500', {
                            'dark:text-icoDarkMode-wolf': !isExport
                        })}
                        icon={`${payload.value}-reaction`}
                    />
                );
            default:
                return (
                    <Icon
                        className={clsx('text-base text-icoGray-500', {
                            'dark:text-icoDarkMode-wolf': !isExport
                        })}
                        icon={`${payload.value}-reaction`}
                    />
                );
        }
    };

    return (
        <foreignObject className="h-4 w-4" x={x - 8} y={y}>
            <div xmlns="http://www.w3.org/1999/xhtml">{customTickContent()}</div>
        </foreignObject>
    );
};

const SimpleBarChartComponent = ({
    data,
    columnNames,
    activeTable,
    userNumbersFormat,
    activeFilter,
    isExport,
    hiddenKeys,
    height = 230
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatHour } = useFormatTimeIndicator();

    return (
        <>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={data.graphData}
                    columnNames={columnNames}
                    userNumbersFormat={userNumbersFormat}
                    valueFormat={data.valueFormat}
                    timeFormat={data.timeFormatter}
                    formatterTranslation={data?.tableColumnFormatterTranslation ?? null}
                    defaultTableSorting={data?.defaultTableSorting ?? null}
                />
            ) : (
                <div
                    className="relative h-full w-full"
                    style={{ minHeight: isExport ? 180 : height }}>
                    <div className="absolute left-0 top-0 h-full w-full">
                        <ResponsiveContainer
                            height={isExport ? 180 : height}
                            width={isExport ? data.fixedSize : '100%'}>
                            <BarChart
                                data={data.graphData}
                                margin={data.margin}
                                barSize={data?.barSize ?? 35}>
                                <CartesianGrid
                                    vertical={false}
                                    stroke={darkModeColorsChart[theme].grid}
                                />
                                <XAxis
                                    dataKey={
                                        data.filters
                                            ? formatXAxisFilters(activeFilter)
                                            : Object.keys(data.name)[0]
                                    }
                                    tickFormatter={(tick) => {
                                        if (data.graphData[0].date_hour) {
                                            return formatHour(tick);
                                        } else if (data.noTickFormat) {
                                            return tick;
                                        }
                                        return t(`${data.tickFormatterTranslation}.${tick}`);
                                    }}
                                    tick={
                                        data?.customTick ? <CustomizedAxisTick data={data} /> : true
                                    }
                                    type="category"
                                    minTickGap={-10}
                                    tickMargin={10}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    tickLine={false}
                                    axisLine={false}
                                    tickCount={5}
                                    tickFormatter={(item) => formatNumber(item, data.valueFormat)}
                                />
                                <Tooltip
                                    cursor={false}
                                    animationDuration={0}
                                    isAnimationActive={false}
                                    content={
                                        <TooltipCustom
                                            data={data}
                                            valueFormat={data.valueFormat}
                                            dateFormat={data.dateFormatter}
                                            tooltipTranslation={data.tooltipTranslation}
                                        />
                                    }
                                />
                                {data.graphData[0].avg && !hiddenKeys?.['avg'] && (
                                    <ReferenceLine
                                        y={data.graphData[0].avg}
                                        stroke={darkModeColorsChart[theme].referenceLine}
                                        strokeWidth={2}
                                    />
                                )}
                                {!hiddenKeys?.['value'] && (
                                    <Bar
                                        isAnimationActive={!isExport}
                                        dataKey="value"
                                        shape={(props) => {
                                            props.radius = [
                                                data?.barRadius ?? 2.5,
                                                data?.barRadius ?? 2.5,
                                                0,
                                                0
                                            ];

                                            if (!hiddenKeys?.[props.reaction]) {
                                                return (
                                                    <Rectangle
                                                        fill={
                                                            data?.multipleColors
                                                                ? darkModeColorsChart[theme][
                                                                      data.color[
                                                                          props[
                                                                              data
                                                                                  ?.multipleColorEntry
                                                                          ]
                                                                      ]
                                                                  ]
                                                                : darkModeColorsChart[theme][
                                                                      data.color?.value
                                                                  ]
                                                        }
                                                        {...props}
                                                    />
                                                );
                                            }
                                        }}
                                    />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </>
    );
};

SimpleBarChartComponent.propTypes = {
    data: PropTypes.object,
    columnNames: PropTypes.object,
    activeTable: PropTypes.bool,
    height: PropTypes.number,
    userNumbersFormat: PropTypes.object,
    isExport: PropTypes.bool,
    hiddenKeys: PropTypes.object
};

const SimpleBarChart = ({
    data,
    onReset = () => {
        /** */
    },
    isLoading = false,
    className = '',
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        legendType="square"
        {...restProps}>
        <SimpleBarChartComponent />
    </ChartWrapper>
);

SimpleBarChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { SimpleBarChart, SimpleBarChartComponent };
